import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { rrFetch } from "@/lib/rehearsalRoomAuth";
import * as tus from "tus-js-client";

/**
 * Master v5 Prompt 11 — Files, versions, Bunny Stream and Bunny Storage.
 * Mirrors practiceQueries.js/sessionQueries.js's `request()` error
 * contract exactly (`.status`/`.body` on thrown errors).
 */
async function request(path, options = {}) {
  const res = await rrFetch(path, options);
  if (res.status === 204) return null;
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const err = new Error((body && (body.detail?.error || body.detail?.message || body.detail)) || "request_failed");
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body;
}

const jsonPost = (path, payload) => request(path, { method: "POST", body: JSON.stringify(payload ?? {}) });

export const fqk = {
  list: (wrkId, targetType, targetId) => ["files", wrkId, targetType || null, targetId || null],
  detail: (wrkId, fasId) => ["file-detail", wrkId, fasId],
};

export function useFileUploadContext(workspaceId) {
  return useQuery({
    queryKey: ["file-upload-context", workspaceId],
    enabled: !!workspaceId,
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/files/upload-context`),
  });
}

export function useFileIndex(workspaceId, { targetType, targetId } = {}) {
  const params = new URLSearchParams();
  if (targetType) params.set("target_type", targetType);
  if (targetId) params.set("target_id", targetId);
  const qs = params.toString();
  return useQuery({
    queryKey: fqk.list(workspaceId, targetType, targetId),
    enabled: !!workspaceId,
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/files${qs ? `?${qs}` : ""}`),
  });
}

export function useFileDetail(workspaceId, fasId) {
  return useQuery({
    queryKey: fqk.detail(workspaceId, fasId),
    enabled: !!workspaceId && !!fasId,
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/files/${fasId}`),
  });
}

export function useSignedUrl(workspaceId, fasId) {
  return useQuery({
    queryKey: ["file-signed-url", workspaceId, fasId],
    enabled: false, // only fetched on explicit "Open" — never cached/reused stale
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/files/${fasId}/signed-url`),
  });
}

export async function fetchSignedUrl(workspaceId, fasId) {
  return request(`/api/rehearsal-room/workspaces/${workspaceId}/files/${fasId}/signed-url`);
}

/**
 * The single, reusable upload orchestrator. `payload` mirrors the
 * backend's UploadInitPayload exactly; `file` is a real DOM File. For a
 * video, this drives a real tus-js-client upload directly to Bunny
 * Stream (onProgress reports 0-100 to `onProgress`); for anything else
 * it proxies the bytes through our own backend. Returns the final
 * {asset, version, link}.
 */
export async function uploadFile({ workspaceId, file, payload, onProgress }) {
  const extension = (file.name.split(".").pop() || "").toLowerCase();
  const init = await request(`/api/rehearsal-room/workspaces/${workspaceId}/files/upload-init`, {
    method: "POST",
    body: JSON.stringify({
      original_filename: file.name, extension, declared_mime_type: file.type || "application/octet-stream",
      size_bytes: file.size, ...payload,
    }),
  });

  if (init.asset.kind === "video") {
    const tusInfo = init.tus_upload;
    await new Promise((resolve, reject) => {
      const upload = new tus.Upload(file, {
        endpoint: tusInfo.endpoint,
        headers: {
          AuthorizationSignature: tusInfo.authorization_signature,
          AuthorizationExpire: String(tusInfo.authorization_expire),
          VideoId: tusInfo.video_id,
          LibraryId: String(tusInfo.library_id),
        },
        metadata: { filetype: file.type || "video/mp4", title: file.name },
        chunkSize: 50 * 1024 * 1024,
        onError: reject,
        onProgress: (sent, total) => onProgress && onProgress(Math.round((sent / total) * 100)),
        onSuccess: resolve,
      });
      upload.start();
    });
    await request(
      `/api/rehearsal-room/workspaces/${workspaceId}/files/${init.asset.fas_id}/versions/${init.version.fav_id}/video-uploaded`,
      { method: "POST", body: JSON.stringify({}) },
    );
    return init;
  }

  const form = new FormData();
  form.append("upload", file, file.name);
  onProgress && onProgress(50);
  const version = await request(
    `/api/rehearsal-room/workspaces/${workspaceId}/files/${init.asset.fas_id}/versions/${init.version.fav_id}/storage-upload`,
    { method: "POST", body: form },
  );
  onProgress && onProgress(100);
  return { ...init, version };
}

export function useReplaceFile(workspaceId, fasId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ file, onProgress }) => {
      const extension = (file.name.split(".").pop() || "").toLowerCase();
      const init = await request(`/api/rehearsal-room/workspaces/${workspaceId}/files/${fasId}/replace-init`, {
        method: "POST", body: JSON.stringify({ extension, declared_mime_type: file.type || "application/octet-stream", size_bytes: file.size }),
      });
      if (init.version.provider === "bunny_stream") {
        const tusInfo = init.tus_upload;
        await new Promise((resolve, reject) => {
          const upload = new tus.Upload(file, {
            endpoint: tusInfo.endpoint,
            headers: {
              AuthorizationSignature: tusInfo.authorization_signature, AuthorizationExpire: String(tusInfo.authorization_expire),
              VideoId: tusInfo.video_id, LibraryId: String(tusInfo.library_id),
            },
            metadata: { filetype: file.type || "video/mp4", title: file.name },
            chunkSize: 50 * 1024 * 1024, onError: reject,
            onProgress: (sent, total) => onProgress && onProgress(Math.round((sent / total) * 100)),
            onSuccess: resolve,
          });
          upload.start();
        });
        await request(`/api/rehearsal-room/workspaces/${workspaceId}/files/${fasId}/versions/${init.version.fav_id}/video-uploaded`, { method: "POST", body: JSON.stringify({}) });
        return init.version;
      }
      const form = new FormData();
      form.append("upload", file, file.name);
      return request(`/api/rehearsal-room/workspaces/${workspaceId}/files/${fasId}/versions/${init.version.fav_id}/storage-upload`, { method: "POST", body: form });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: fqk.detail(workspaceId, fasId) });
      qc.invalidateQueries({ queryKey: ["files", workspaceId] });
    },
  });
}

export function useCreateFileLink(workspaceId, fasId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/files/${fasId}/links`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: fqk.detail(workspaceId, fasId) });
      qc.invalidateQueries({ queryKey: ["files", workspaceId] });
    },
  });
}

export function useRemoveFileLink(workspaceId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ fasId, flnId }) => request(`/api/rehearsal-room/workspaces/${workspaceId}/files/${fasId}/links/${flnId}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["files", workspaceId] }),
  });
}

export function useCreateFileRoomLaunch(workspaceId) {
  return useMutation({
    mutationFn: (fasId) => jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/files/${fasId}/room`),
  });
}

export function useMarkFileReviewed(workspaceId) {
  return useMutation({
    mutationFn: (fasId) => jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/files/${fasId}/mark-reviewed`),
  });
}

// Master v5 Prompt 19 — Files workbench archive/unarchive (soft, reversible).
export function useArchiveFile(workspaceId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fasId) => jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/files/${fasId}/archive`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["files", workspaceId] }); qc.invalidateQueries({ queryKey: ["coach-files-workbench"] }); },
  });
}

export function useUnarchiveFile(workspaceId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fasId) => jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/files/${fasId}/unarchive`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["files", workspaceId] }); qc.invalidateQueries({ queryKey: ["coach-files-workbench"] }); },
  });
}

// Prompt 30D — permanent delete, only ever offered once a File is
// already archived (see file_routes.delete_file_route).
export function useDeleteFile(workspaceId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fasId) => request(`/api/rehearsal-room/workspaces/${workspaceId}/files/${fasId}`, { method: "DELETE" }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["files", workspaceId] }); qc.invalidateQueries({ queryKey: ["coach-files-workbench"] }); },
  });
}
